import { useEffect } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { mediaUrl } from "@/utils/mediaUrl";

/**
 * Tam ekran medya görüntüleyici (lightbox).
 *
 * Foto/video listesini büyük gösterir; ileri/geri ile gezilir. Yeni sekme açmaz.
 *
 * Props:
 *  - items: [{ url: "/uploads/...", type: "photo"|"video" }]
 *  - index: aktif öğenin indexi
 *  - apiBase: url önüne eklenecek backend adresi (BACKEND_URL)
 *  - onClose(): kapatma
 *  - onNavigate(newIndex): index değişimi
 */
export function MediaLightbox({ items = [], index = 0, apiBase = "", onClose, onNavigate }) {
  const count = items.length;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "ArrowLeft" && count > 1) onNavigate?.((index - 1 + count) % count);
      else if (e.key === "ArrowRight" && count > 1) onNavigate?.((index + 1) % count);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, count, onClose, onNavigate]);

  const item = items[index];
  if (!item) return null;
  // `/uploads/...` doğrudan istenirse oturum çerezi (path=/api) gitmez → 401.
  const src = mediaUrl(apiBase, item.url);

  const go = (e, dir) => {
    e.stopPropagation();
    onNavigate?.((index + dir + count) % count);
  };

  return (
    <div
      className="fixed inset-0 z-[10000] bg-black/90 flex items-center justify-center"
      onClick={onClose}
    >
      {/* Kapat */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose?.(); }}
        className="absolute top-4 right-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/30"
        aria-label="Kapat"
        title="Kapat (Esc)"
      >
        <X className="h-6 w-6" />
      </button>

      {/* Geri */}
      {count > 1 && (
        <button
          type="button"
          onClick={(e) => go(e, -1)}
          className="absolute left-3 z-10 flex h-12 w-12 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/30"
          aria-label="Önceki"
        >
          <ChevronLeft className="h-7 w-7" />
        </button>
      )}

      {/* Medya */}
      <div className="max-w-[92vw] max-h-[88vh]" onClick={(e) => e.stopPropagation()}>
        {item.type === "photo" ? (
          <img src={src} alt="" className="max-w-[92vw] max-h-[88vh] object-contain" />
        ) : (
          <video src={src} controls autoPlay muted playsInline className="max-w-[92vw] max-h-[88vh] object-contain bg-black" />
        )}
      </div>

      {/* İleri */}
      {count > 1 && (
        <button
          type="button"
          onClick={(e) => go(e, 1)}
          className="absolute right-3 z-10 flex h-12 w-12 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/30"
          aria-label="Sonraki"
        >
          <ChevronRight className="h-7 w-7" />
        </button>
      )}

      {/* Sayaç */}
      {count > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-white/15 px-3 py-1 text-sm text-white">
          {index + 1} / {count}
        </div>
      )}
    </div>
  );
}

export default MediaLightbox;
